import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ListFilter, Search, Check } from 'lucide-react'
import { cn } from '../../utils/cn'
import type { Option } from '../../lib/paymentReport/filterOptions'

interface HeaderFilterProps {
  label: string
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  /** Right-align the popover under the trigger (for right-aligned columns). */
  align?: 'left' | 'right'
}

const DROPDOWN_HEIGHT = 320 // max-h-80
const DROPDOWN_WIDTH = 240
const RENDER_CAP = 100      // "showing top 100 of N" — keeps the list light

// Excel-style column header with a multi-select dropdown. Purely presentational:
// it knows nothing about queries — the parent owns option sourcing and predicates.
// Popover mechanics mirror AccountCombobox (portal + fixed positioning + flip +
// click-outside close + focus-on-open search).
export function HeaderFilter({ label, options, selected, onChange, align = 'left' }: HeaderFilterProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false })

  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const active = selected.length > 0
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => o.label.toLowerCase().includes(q))
  }, [options, search])

  const shown = filtered.slice(0, RENDER_CAP)
  const overflow = filtered.length - shown.length

  const updatePosition = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openUp = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow
    const rawLeft = align === 'right' ? rect.right - DROPDOWN_WIDTH : rect.left
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - DROPDOWN_WIDTH - 8))
    setPos({ top: openUp ? rect.top - DROPDOWN_HEIGHT - 4 : rect.bottom + 4, left, openUp })
  }

  useLayoutEffect(() => {
    if (open) updatePosition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onMove = () => updatePosition()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (dropdownRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    setSearch('')
    const id = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  function toggle(value: string) {
    if (selectedSet.has(value)) onChange(selected.filter(v => v !== value))
    else onChange([...selected, value])
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide transition-colors',
          active ? 'text-brand-600' : 'text-gray-400 hover:text-gray-600',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{label}</span>
        <ListFilter className={cn('h-3 w-3', active && 'fill-brand-100')} />
        {active && (
          <span className="rounded-full bg-brand-100 px-1 text-[10px] font-semibold text-brand-700">
            {selected.length}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: DROPDOWN_WIDTH, zIndex: 60 }}
            className="flex max-h-80 flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
          >
            <div className="flex items-center gap-2 border-b border-gray-100 px-3">
              <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent py-2.5 text-sm normal-case tracking-normal text-gray-900 placeholder-gray-400 outline-none"
              />
            </div>

            {active && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="border-b border-gray-100 px-3 py-2 text-left text-xs font-medium text-brand-600 hover:bg-brand-50"
              >
                Clear ({selected.length})
              </button>
            )}

            <div role="listbox" className="overflow-y-auto">
              {shown.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-gray-400">No values match</div>
              ) : (
                shown.map(o => {
                  const isSel = selectedSet.has(o.value)
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      onMouseDown={e => {
                        e.preventDefault()
                        toggle(o.value)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm normal-case tracking-normal transition-colors',
                        isSel ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                          isSel ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-300',
                        )}
                      >
                        {isSel && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate">{o.label}</span>
                    </button>
                  )
                })
              )}
              {overflow > 0 && (
                <div className="px-3 py-2 text-center text-[11px] text-gray-400">
                  Showing top {RENDER_CAP} of {filtered.length} — refine your search
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
